import { formatDistanceToNowStrict } from 'date-fns';
import React, { forwardRef } from 'react';
import { Box, Flex, Text } from 'rebass';
import type { ActivityFeedItem as ActivityFeedItemData } from './ActivityFeed.queries';
import ActivityActionButtons from './ActivityActionButtons';

export type ActivityFeedItemProps = {
  notification: ActivityFeedItemData;
  index: number;
  onSelectItem: (notification: ActivityFeedItemData) => void;
  onMarkRead: (notificationId: string | number) => Promise<void>;
  onMarkUnread: (notificationId: string | number) => Promise<void>;
  onMarkAsFollowUp: (notificationId: string | number) => Promise<void>;
  onUnMarkAsFollowUp: (notificationId: string | number) => Promise<void>;
  onDismiss: (notificationId: string | number) => Promise<void>;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>, index: number) => void;
  role?: string;
  tabIndex?: number;
  variant?: 'menu' | 'panel';
};

const ActivityFeedItem = forwardRef<HTMLButtonElement, ActivityFeedItemProps>(
  function ActivityFeedItem(
    {
      notification,
      index,
      onSelectItem,
      onMarkRead,
      onMarkUnread,
      onMarkAsFollowUp,
      onUnMarkAsFollowUp,
      onDismiss,
      onKeyDown,
      role = 'button',
      tabIndex,
      variant = 'menu',
    },
    ref
  ) {
    const baseBackground = !notification.read
      ? 'highlight'
      : variant === 'menu'
        ? 'navbar'
        : 'white';

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          minWidth: 0,
          borderBottom: '1px solid',
          borderColor: 'tableBorder',
          bg: baseBackground,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            bg: 'tableHover',
          },
          '&:hover .activity-feed-item-time': {
            visibility: 'hidden',
          },
          '&:hover .activity-feed-item-actions': {
            display: 'flex',
          },
          '&:last-of-type': {
            borderBottom: 'none',
          },
        }}
      >
        <Box
          ref={ref}
          as="button"
          type="button"
          role={role}
          tabIndex={tabIndex}
          onClick={() => {
            onSelectItem(notification);
          }}
          onKeyDown={
            onKeyDown
              ? (event: React.KeyboardEvent<HTMLElement>) =>
                  onKeyDown(event, index)
              : undefined
          }
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            pt: 3,
            pb: 3,
            pl: 3,
            pr: 2,
            textAlign: 'left',
            cursor: 'pointer',
            border: 'none',
            bg: 'transparent',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary',
              outlineOffset: '-2px',
            },
            display: 'grid',
            gridTemplateColumns: '8px minmax(0, 1fr)',
            gridTemplateRows: 'auto auto',
            columnGap: 1,
            rowGap: 1,
          }}
        >
          <Box
            sx={{
              gridRow: 1,
              gridColumn: 1,
              alignSelf: 'center',
              flexShrink: 0,
              justifySelf: 'center',
              width: 6,
              height: 6,
              borderRadius: 'circle',
              bg: notification.followUp
                ? 'statusOrange'
                : !notification.read
                  ? 'primary'
                  : 'transparent',
            }}
          />
          <Flex
            alignItems="baseline"
            sx={{
              gridRow: 1,
              gridColumn: 2,
              minWidth: 0,
              width: '100%',
              gap: 2,
            }}
          >
            <Text
              variant="body2"
              fontWeight={!notification.read ? 'bold' : 'body'}
              color="text"
              title={notification.title}
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontStyle: notification.followUp ? 'italic' : 'normal',
              }}
            >
              {notification.title}
            </Text>
            <Text
              className="activity-feed-item-time"
              variant="body3"
              color="onNavbar"
              sx={{
                flex: '0 0 auto',
                minWidth: 76,
                textAlign: 'right',
                fontSize: 0,
                lineHeight: '16px',
              }}
            >
              {formatDistanceToNowStrict(new Date(notification.time), {
                addSuffix: true,
              })}
            </Text>
          </Flex>
          <Text
            variant="body2"
            color="onNavbar"
            title={notification.body}
            sx={{
              gridRow: 2,
              gridColumn: '2 / -1',
              minWidth: 0,
              mt: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {notification.body}
          </Text>
        </Box>
        <Flex
          sx={{
            position: 'absolute',
            top: '11px',
            right: '2px',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
          }}
        >
          <Box
            className="activity-feed-item-actions"
            sx={{
              display: 'none',
              mt: '-2px',
            }}
          >
            <ActivityActionButtons
              notification={notification}
              onMarkRead={onMarkRead}
              onMarkUnread={onMarkUnread}
              onMarkAsFollowUp={onMarkAsFollowUp}
              onUnMarkAsFollowUp={onUnMarkAsFollowUp}
              onDismiss={onDismiss}
            />
          </Box>
        </Flex>
      </Box>
    );
  }
);

export default ActivityFeedItem;
